import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SeoHead from '../../components/feature/SeoHead';
import { MatchDemo, ScreeningDemo, TitleDemo, PromoDemo, ChatDemo, PriceDemo } from './components/AiDemos';
import AiFaq from './components/AiFaq';
import AiBenefitsSection from './components/AiBenefitsSection';

const AI_FEATURES = [
  {
    id: 'match',
    icon: 'ri-sparkling-2-fill',
    color: 'teal',
    tag: '核心功能',
    title: 'AI 智能配對',
    subtitle: '讓 AI 幫你找到最合適的那一個',
    desc: '根據租客填寫的需求（地點、預算、房型、生活習慣），AI 系統即時分析資料庫中所有符合條件的房源，計算媒合分數後直接推送給最適合的租客。屋主也能主動瀏覽等待名單。',
    points: ['多維度條件分析', '即時媒合推播', '租客等待名單', '屋主主動邀約'],
    to: '/ai-match',
    cta: '立即體驗 AI 媒合',
    Demo: MatchDemo,
  },
  {
    id: 'screening',
    icon: 'ri-shield-check-line',
    color: 'green',
    tag: '屋主專屬',
    title: 'AI 租客審核',
    subtitle: '一鍵分析租客資訊，降低出租風險',
    desc: '屋主可對有意向的租客觸發 AI 審核，系統根據互動記錄與基本資訊自動產出審核報告，提供風險評估與建議，讓屋主在見面前就能做出更有把握的決定。',
    points: ['AI 風險評估報告', '互動紀錄分析', '租客可信度評分', '出租決策建議'],
    to: '/landlord-dashboard',
    cta: '前往屋主管理台',
    Demo: ScreeningDemo,
  },
  {
    id: 'title',
    icon: 'ri-file-text-line',
    color: 'amber',
    tag: '發布加速',
    title: 'AI 房源標題生成',
    subtitle: '10 秒產出吸睛標題，點閱率翻倍',
    desc: '發布房源時，填完基本資訊後只需點一下「AI 生成」，系統即自動分析房源特色（地段、格局、設備），產出多個符合 SEO 與用戶搜尋習慣的吸睛標題，大幅減少撰寫時間。',
    points: ['一鍵生成多個標題', '融合地段與亮點', 'SEO 友善語言', '秒選秒發布'],
    to: '/publish',
    cta: '立即發布房源',
    Demo: TitleDemo,
  },
  {
    id: 'promo',
    icon: 'ri-megaphone-line',
    color: 'orange',
    tag: '行銷自動化',
    title: 'AI 推廣文案生成',
    subtitle: '自動撰寫 Threads / Facebook 推廣文',
    desc: '一個房源，多個平台推廣。AI 根據房源特色自動生成適合社群媒體的推廣文案，支援 Threads 及 Facebook 格式，可直接一鍵發布或設定定時排程，觸及更多潛在租客。',
    points: ['Threads 貼文自動生成', 'Facebook 貼文草稿', '定時排程發布', 'AI 留言回覆'],
    to: '/landlord-dashboard',
    cta: '前往管理台試用',
    Demo: PromoDemo,
  },
  {
    id: 'chat',
    icon: 'ri-robot-2-line',
    color: 'emerald',
    tag: '即時服務',
    title: 'AI 租屋客服問答',
    subtitle: '24 小時線上，秒回租客各種問題',
    desc: '每間房源詳細頁面都搭載 AI 問答機器人，租客可用自然語言詢問房源細節、附近環境、交通距離等問題，AI 即時給出精確回答，屋主不在線也不漏接任何意向客。',
    points: ['自然語言理解', '房源細節即時解答', '附近環境查詢', '24/7 不斷線服務'],
    to: '/regions',
    cta: '瀏覽房源試試看',
    Demo: ChatDemo,
  },
  {
    id: 'price',
    icon: 'ri-bar-chart-box-line',
    color: 'rose',
    tag: '市場分析',
    title: 'AI 實價查詢分析',
    subtitle: '即時掌握周邊行情，定價不再靠感覺',
    desc: '整合政府實價登錄資料，AI 自動分析周邊同類型房源的成交行情，提供建議定價區間。屋主設定租金前只需輸入地址，即可獲得數據支撐的定價建議，避免定價偏差。',
    points: ['實價登錄資料整合', '周邊行情分析', 'AI 建議定價區間', '可視化趨勢圖表'],
    to: '/landlord-dashboard',
    cta: '前往管理台查詢',
    Demo: PriceDemo,
  },
];

const COLOR_MAP: Record<string, {
  bg: string; border: string; text: string;
  tag: string; tagText: string; point: string; btn: string; glow: string;
  quickBg: string; quickBorder: string;
}> = {
  teal:    { bg: 'bg-teal-500/8',    border: 'border-teal-500/20',    text: 'text-teal-400',    tag: 'bg-teal-500/15 border-teal-500/30',    tagText: 'text-teal-300',    point: 'bg-teal-400',    btn: 'bg-teal-500 hover:bg-teal-400 text-white',    glow: 'rgba(45,212,191,0.10)',  quickBg: 'bg-teal-500/15',   quickBorder: 'border-teal-500/25' },
  green:   { bg: 'bg-green-500/8',   border: 'border-green-500/20',   text: 'text-green-400',   tag: 'bg-green-500/15 border-green-500/30',   tagText: 'text-green-300',   point: 'bg-green-400',   btn: 'bg-green-600 hover:bg-green-500 text-white',   glow: 'rgba(74,222,128,0.08)',  quickBg: 'bg-green-500/15',  quickBorder: 'border-green-500/25' },
  amber:   { bg: 'bg-amber-500/8',   border: 'border-amber-500/20',   text: 'text-amber-400',   tag: 'bg-amber-500/15 border-amber-500/30',   tagText: 'text-amber-300',   point: 'bg-amber-400',   btn: 'bg-amber-500 hover:bg-amber-400 text-white',   glow: 'rgba(251,191,36,0.08)',  quickBg: 'bg-amber-500/15',  quickBorder: 'border-amber-500/25' },
  orange:  { bg: 'bg-orange-500/8',  border: 'border-orange-500/20',  text: 'text-orange-400',  tag: 'bg-orange-500/15 border-orange-500/30',  tagText: 'text-orange-300',  point: 'bg-orange-400',  btn: 'bg-orange-500 hover:bg-orange-400 text-white',  glow: 'rgba(249,115,22,0.08)',  quickBg: 'bg-orange-500/15', quickBorder: 'border-orange-500/25' },
  emerald: { bg: 'bg-emerald-500/8', border: 'border-emerald-500/20', text: 'text-emerald-400', tag: 'bg-emerald-500/15 border-emerald-500/30', tagText: 'text-emerald-300', point: 'bg-emerald-400', btn: 'bg-emerald-600 hover:bg-emerald-500 text-white', glow: 'rgba(52,211,153,0.08)',  quickBg: 'bg-emerald-500/15',quickBorder: 'border-emerald-500/25' },
  rose:    { bg: 'bg-rose-500/8',    border: 'border-rose-500/20',    text: 'text-rose-400',    tag: 'bg-rose-500/15 border-rose-500/30',    tagText: 'text-rose-300',    point: 'bg-rose-400',    btn: 'bg-rose-500 hover:bg-rose-400 text-white',    glow: 'rgba(244,63,94,0.08)',   quickBg: 'bg-rose-500/15',   quickBorder: 'border-rose-500/25' },
};

const STATS = [
  { icon: 'ri-money-dollar-circle-line', color: 'text-teal-400',   value: '0 元',  label: '所有 AI 功能費用' },
  { icon: 'ri-sparkling-2-fill',         color: 'text-amber-400',  value: '6 項',  label: 'AI 核心功能' },
  { icon: 'ri-time-line',                color: 'text-emerald-400',value: '10 秒', label: 'AI 標題生成速度' },
  { icon: 'ri-service-line',             color: 'text-rose-400',   value: '24/7',  label: '客服 AI 不斷線' },
];

function useCountUp(target: number, duration = 1200, trigger: boolean) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!trigger) return;
    let start = 0;
    const step = target / (duration / 16);
    const iv = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(iv); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(iv);
  }, [trigger, target, duration]);
  return count;
}

export default function AiFeaturesPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const statsRef = useRef<HTMLDivElement>(null);
  const [statsVisible, setStatsVisible] = useState(false);
  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(([e]) => { if (e.isIntersecting) setStatsVisible(true); }, { threshold: 0.3 });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const scrollToFeature = (id: string) => {
    document.getElementById(`feature-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <SeoHead
        title="藍舍AI地產 6大AI功能全解析｜免費智能配對、標題生成、租客審核"
        description="藍舍AI地產平台完整 AI 功能介紹：智能配對、租客審核、標題生成、推廣文案、即時問答、實價分析，全部免費，全方位 AI 助力台灣租屋。"
        keywords="AI租屋,AI配對,AI房源,藍舍AI地產,智能審核"
        canonical="/ai-features"
      />
      <Navbar />

      {/* ══ Hero ══ */}
      <div className="relative overflow-hidden">
        {/* 背景圖 */}
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/151c07b744b098939918ad94eec3763d.jpg"
            alt="AI 功能背景"
            className="w-full h-full object-cover object-center"
            loading="eager"
          />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg,rgba(3,7,18,.88) 0%,rgba(3,7,18,.72) 50%,rgba(3,7,18,.90) 100%)' }} />
          <div className="absolute top-0 left-1/3 w-[500px] h-[500px] bg-teal-500/8 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-emerald-500/6 rounded-full blur-3xl pointer-events-none" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 pt-28 pb-14 md:pt-36 md:pb-18 text-center">
          {/* Headline */}
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-black mb-5 leading-tight tracking-tight">
            6 個 AI 功能<br />
            <span className="text-teal-400">重新定義台灣租屋</span>
          </h1>
          <p className="text-white/50 text-sm md:text-base leading-relaxed max-w-xl mx-auto mb-8">
            從找房、發布、審核、定價到行銷，藍舍 AI 地產將人工智慧深度整合進每個關鍵環節。<br className="hidden md:block" />
            以下每個功能都有<strong className="text-white/75">即時動畫示範</strong>，讓你親眼看見 AI 如何運作。
          </p>

          {/* Stats */}
          <div className="flex items-center justify-center gap-6 md:gap-12 mb-8">
            {[
              { num: '6', sub: 'AI 核心功能' },
              { num: '0元', sub: '完全免費' },
              { num: '24/7', sub: '不斷線服務' },
            ].map((s) => (
              <div key={s.sub} className="text-center">
                <p className="text-2xl md:text-3xl font-black text-teal-400 leading-none">{s.num}</p>
                <p className="text-[10px] text-white/30 mt-1">{s.sub}</p>
              </div>
            ))}
          </div>

          {/* Quick nav pills - 換行置中，不捲動 */}
          <div className="flex flex-wrap items-center justify-center gap-2">
            {AI_FEATURES.map((f) => {
              const c = COLOR_MAP[f.color];
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => scrollToFeature(f.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[11px] font-semibold transition-all cursor-pointer hover:scale-105 whitespace-nowrap ${c.quickBg} ${c.quickBorder} ${c.text}`}
                >
                  <i className={`${f.icon} text-xs`}></i>{f.title}
                </button>
              );
            })}
          </div>
        </div>

        {/* bottom fade */}
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-b from-transparent to-gray-950 pointer-events-none" />
      </div>

      {/* ══ 平台優勢 Stats Bar ══ */}
      <div ref={statsRef} className="max-w-5xl mx-auto px-4 py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {STATS.map((s) => (
            <div key={s.label} className="flex flex-col items-center gap-2 py-5 rounded-2xl border border-white/8 bg-white/3">
              <div className={`w-10 h-10 flex items-center justify-center rounded-xl bg-white/6`}>
                <i className={`${s.icon} text-xl ${s.color}`}></i>
              </div>
              <p className={`text-xl font-black ${s.color} leading-none`}>{s.value}</p>
              <p className="text-[10px] text-white/35 text-center leading-snug">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="h-px bg-gradient-to-r from-transparent via-teal-500/20 to-transparent mb-2"></div>

      {/* ══ 功能詳解 ══ */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/6 border border-white/10 rounded-full text-[10px] font-bold text-white/50 mb-3">
            <i className="ri-play-circle-line text-xs animate-pulse"></i>
            附即時互動示範
          </div>
          <h2 className="text-xl md:text-2xl font-black text-white">每項功能都有 LIVE 演示</h2>
          <p className="text-white/35 text-sm mt-2">向下捲動，親眼看見 AI 如何幫你處理租屋每一個環節</p>
        </div>

        <div className="space-y-4">
          {AI_FEATURES.map((f, idx) => {
            const c = COLOR_MAP[f.color];
            const Demo = f.Demo;
            const isEven = idx % 2 === 0;
            return (
              <div
                key={f.id}
                id={`feature-${f.id}`}
                className={`rounded-2xl border overflow-hidden ${c.border}`}
                style={{ background: `linear-gradient(135deg, ${c.glow} 0%, rgba(0,0,0,0) 60%), rgba(255,255,255,0.03)`, scrollMarginTop: '80px' }}
              >
                {/* ── 頂部標籤列 ── */}
                <div className="flex items-center gap-3 px-4 pt-4 pb-0">
                  <span className={`text-[10px] font-black px-2.5 py-1 rounded-full border ${c.tag} ${c.tagText}`}>{f.tag}</span>
                  <span className="text-white/20 text-xs">#{String(idx + 1).padStart(2, '0')}</span>
                  <div className="flex-1 h-px bg-white/6 ml-1"></div>
                  <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full border ${c.tag}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${c.point} animate-pulse`}></span>
                    <span className={`text-[9px] font-bold ${c.tagText}`}>LIVE</span>
                  </div>
                </div>

                {/* ── 手機版：Demo 優先，文字在下 ── */}
                <div className="md:hidden p-4 space-y-4">
                  {/* Title row */}
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-xl border ${c.bg} ${c.border}`}>
                      <i className={`${f.icon} text-base ${c.text}`}></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-black text-white leading-tight">{f.title}</h3>
                      <p className={`text-[11px] font-medium mt-0.5 ${c.text}`}>{f.subtitle}</p>
                    </div>
                  </div>

                  {/* Demo 優先展示 */}
                  <Demo />

                  {/* 說明文字 - 手機縮短顯示 */}
                  <p className="text-white/45 text-xs leading-relaxed line-clamp-3">{f.desc}</p>

                  {/* 功能點：2欄緊湊 */}
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                    {f.points.map((pt) => (
                      <div key={pt} className="flex items-center gap-1.5">
                        <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${c.point}`}></div>
                        <span className="text-[11px] text-white/50 leading-tight">{pt}</span>
                      </div>
                    ))}
                  </div>

                  {/* CTA */}
                  <Link
                    to={f.to}
                    className={`flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${c.btn}`}
                  >
                    {f.cta}
                    <i className="ri-arrow-right-line text-sm"></i>
                  </Link>
                </div>

                {/* ── 桌機版：左右交錯佈局 ── */}
                <div className={`hidden md:flex ${isEven ? 'flex-row' : 'flex-row-reverse'} gap-5 p-5`}>
                  {/* 文字區 */}
                  <div className="flex-1 flex flex-col justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-xl border ${c.bg} ${c.border}`}>
                          <i className={`${f.icon} text-lg ${c.text}`}></i>
                        </div>
                        <div>
                          <h3 className="text-base md:text-lg font-black text-white leading-tight">{f.title}</h3>
                          <p className={`text-xs font-medium mt-0.5 ${c.text}`}>{f.subtitle}</p>
                        </div>
                      </div>
                      <p className="text-white/50 text-sm leading-relaxed">{f.desc}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                      {f.points.map((pt) => (
                        <div key={pt} className="flex items-center gap-1.5">
                          <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${c.point}`}></div>
                          <span className="text-xs text-white/50">{pt}</span>
                        </div>
                      ))}
                    </div>
                    <Link
                      to={f.to}
                      className={`self-start inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap hover:-translate-y-0.5 ${c.btn}`}
                    >
                      {f.cta}
                      <i className="ri-arrow-right-line text-sm"></i>
                    </Link>
                  </div>

                  {/* Demo 視窗 */}
                  <div className="md:w-[42%] flex-shrink-0">
                    <Demo />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="h-px bg-gradient-to-r from-transparent via-white/8 to-transparent my-4"></div>

      {/* ══ 適合誰 ══ */}
      <AiBenefitsSection />

      <div className="h-px bg-gradient-to-r from-transparent via-white/8 to-transparent my-4"></div>

      {/* ══ FAQ ══ */}
      <AiFaq />

      {/* ══ 底部 CTA ══ */}
      <div className="max-w-5xl mx-auto px-4 pb-20">
        <div className="relative overflow-hidden rounded-3xl border border-teal-500/20 p-8 md:p-12 text-center"
          style={{ background: 'linear-gradient(135deg, rgba(45,212,191,0.10) 0%, rgba(16,185,129,0.06) 50%, rgba(45,212,191,0.08) 100%)' }}>
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-32 bg-teal-500/10 blur-3xl pointer-events-none" />
          <div className="relative z-10">
            <div className="w-16 h-16 rounded-2xl bg-teal-500/20 border border-teal-500/25 flex items-center justify-center mx-auto mb-5">
              <i className="ri-sparkling-2-fill text-amber-400 text-3xl animate-pulse"></i>
            </div>
            <h3 className="text-2xl md:text-3xl font-black text-white mb-2">準備好體驗 AI 租屋了嗎？</h3>
            <p className="text-white/40 text-sm mb-2">免費使用 · 全功能開放 · 無需下載 App</p>
            <p className="text-white/25 text-xs mb-8">台灣唯一深度整合 AI 的零仲介直租平台</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link to="/ai-match"
                className="flex items-center gap-2 px-7 py-3.5 bg-teal-500 hover:bg-teal-400 text-white text-sm font-bold rounded-xl transition-all cursor-pointer whitespace-nowrap hover:-translate-y-0.5">
                <i className="ri-sparkling-2-fill"></i>AI 智能配對找房
              </Link>
              <Link to="/publish"
                className="flex items-center gap-2 px-7 py-3.5 bg-white/8 hover:bg-white/14 border border-white/15 text-white text-sm font-bold rounded-xl transition-all cursor-pointer whitespace-nowrap hover:-translate-y-0.5">
                <i className="ri-home-4-line"></i>發布房源
              </Link>
              <Link to="/register"
                className="flex items-center gap-2 px-7 py-3.5 bg-white/8 hover:bg-white/14 border border-white/15 text-white text-sm font-bold rounded-xl transition-all cursor-pointer whitespace-nowrap hover:-translate-y-0.5">
                <i className="ri-user-add-line"></i>免費註冊
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
